// LOCAL: mounted above the app shell (around AppLayout + Routes) so both the
// sidebar and the store pages read the active store from one place. The store
// id comes from the URL via `useMatch` — the provider sits above `<Routes>`, so
// there's no route-param context to read; this is the single spot that owns
// that lookup + the `['store', storeId]` query + recent-store tracking.
//
// Two access patterns:
//  - `useActiveStore()` — nullable; safe anywhere (e.g. the sidebar, which
//    renders on non-store routes too).
//  - `useStoreContext()` / `useStoreId()` — assert a loaded store; only valid
//    under a store route, where `StoreLayout` gates rendering behind the query.
import { useQuery } from '@tanstack/react-query';
import { CommandAdminApi } from 'corso-types';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { useMatch } from 'react-router-dom';
import api from '~/api';
import { useTrackRecentStore } from '~/hooks/useRecentStores';

type Store = CommandAdminApi.ResponseBody<'/store/:storeId', 'get'>;

type ActiveStore = {
  /** Active store id from the URL, or `undefined` off a `/stores/:storeId` route. */
  storeId: number | undefined;
  /** Loaded store, or `undefined` while pending / when no store is active. */
  store: Store | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  error: Error | null;
};

const ActiveStoreContext = createContext<ActiveStore | null>(null);

function useActiveStoreContext() {
  const ctx = useContext(ActiveStoreContext);
  if (!ctx) {
    throw new Error(
      'ActiveStoreContext unavailable. Did you forget to mount <StoreProvider>?',
    );
  }
  return ctx;
}

/** Nullable active-store accessor — safe on any route under the provider. */
export const useActiveStore = () => useActiveStoreContext();

/** The loaded store for the current `/stores/:storeId` subtree. Throws elsewhere. */
export function useStoreContext() {
  const { store } = useActiveStoreContext();
  if (!store) {
    throw new Error(
      'No active store. useStoreContext must be used within a store route.',
    );
  }
  return store;
}

export function useRefetchStore() {
  const { refetch } = useActiveStoreContext();
  return refetch;
}

/** The active `storeId` for the current `/stores/:storeId` subtree. */
export const useStoreId = () => useStoreContext().id;

export function StoreProvider({ children }: PropsWithChildren) {
  const match = useMatch('/stores/:storeId/*');
  const parsed = Number(match?.params.storeId);
  const storeId = Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['store', storeId],
    queryFn: () => api.store(storeId).get(),
    enabled: storeId !== undefined,
  });

  useTrackRecentStore(data ? { id: data.id, name: data.name } : undefined);

  const value = useMemo(
    () => ({
      storeId,
      store: storeId === undefined ? undefined : data,
      isPending,
      isError,
      error,
      refetch,
    }),
    [storeId, data, isPending, isError, error, refetch],
  );

  return (
    <ActiveStoreContext.Provider value={value}>
      {children}
    </ActiveStoreContext.Provider>
  );
}
